import { eventUrl } from "@/lib/stripe-links";

// ── UnifiedEvent ────────────────────────────────────────────────────────────

export type EventSource = "platform" | "connected" | "store";

export interface UnifiedEvent {
  source: EventSource;
  id: string;
  type: string;
  created: number; // unix seconds
  account: string | null;
  livemode: boolean | null;
  apiVersion: string | null;
  request: { id: string | null; idempotencyKey: string | null } | null;
  data: { object: unknown; previousAttributes: unknown | null };
  eventSourceType: string | null; // platform only
  country: string | null; // platform only
  /** `apple` / `google`. Store rows only. */
  provider: string | null;
  /**
   * What the store said this row's environment is. Store rows only.
   *
   * `unknown` is a real value rather than missing data: an RTDN carries no
   * environment marker at all, so recording anything else would be a lie the
   * inspector would present as a fact.
   */
  environment: string | null;
  // v2 processing fields
  processingStatus: ProcessingStatus | null;
  processingError: string | null;
  processedAt: number | null;
  processingAttempts: ProcessingAttempt[];
}

// ── BusinessSummary ─────────────────────────────────────────────────────────

export interface BusinessSummary {
  id: string;
  businessName: string;
  stripeConnectedAccountId: string | null;
}

// ── Processing Status ──────────────────────────────────────────────────────

export type ProcessingStatus =
  | "pending"
  | "success"
  | "failed"
  | "unhandled"
  // Store rows only. `skipped` is a notification for another deployment under
  // Pub/Sub fan-out, and `parse_error` a body that will never parse — both
  // terminal, and neither a failure worth chasing.
  | "skipped"
  | "parse_error"
  // Also terminal, but the one worth counting rather than ignoring: the
  // purchase is bound to another business, and a store identifier is permanent.
  | "rejected_binding";

export interface ProcessingAttempt {
  attemptedAt: number;
  success: boolean;
  error: string | null;
  trigger: "stream" | "replay";
  durationMs: number;
  handlerName: string;
  lambdaRequestId: string;
  replayedBy: string | null;
}

// ── Filters ─────────────────────────────────────────────────────────────────

export type DateRangePreset = "1h" | "24h" | "7d" | "30d" | "all" | "custom";

export interface CustomDateRange {
  from: number; // unix seconds
  to: number; // unix seconds
}

export type DateRangeFilter =
  | { preset: Exclude<DateRangePreset, "custom"> }
  | { preset: "custom"; range: CustomDateRange };

export interface EventFilters {
  typeQuery: string; // substring, client-side
  dateRange: DateRangeFilter;
  livemode: "all" | "true" | "false";
  account: string | null; // Connected tab only
  statusFilter: ProcessingStatus[]; // empty = show all
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * The Stripe dashboard URL for an event, or `null` when it has none.
 *
 * Store notifications come from Apple and Google and have no Stripe
 * representation at all. Returning `null` rather than a plausible-looking URL
 * keeps the caller from rendering a link that 404s in someone else's dashboard.
 */
export function stripeDashboardUrl(event: UnifiedEvent): string | null {
  if (event.source === "store") return null;

  return eventUrl(event.id, {
    livemode: event.livemode,
    source: event.source,
    account: event.account,
  });
}

/**
 * Returns the start-of-range unix timestamp (seconds) for a preset.
 * Returns 0 for "all" (no lower bound — callers should skip the date filter).
 */
export function presetToFromTimestamp(
  preset: Exclude<DateRangePreset, "custom">,
): number {
  const now = Math.floor(Date.now() / 1000);
  switch (preset) {
    case "1h":
      return now - 3600;
    case "24h":
      return now - 86400;
    case "7d":
      return now - 7 * 86400;
    case "30d":
      return now - 30 * 86400;
    case "all":
      return 0;
  }
}
